import FocusManager from '../utils/focus-manager';
import ScrollManager from '../utils/scroll-manager';

export default function initNavigation() {
    new HeaderNavigation(document.getElementById('main-header'));
}

class HeaderNavigation {
    private applicableHeaderTransparent = [
      'home',
      'page',
      'error404',
      'archive',
      'single-syndicat-adherent',
      'single-convention',
      'blog',
      'search'
    ];
    private $el: HTMLElement | null;
    private $body: HTMLElement | null;
    private $menuToggler: HTMLButtonElement | null;
    private $mainNav: HTMLElement | null;
    private $mainNavContent: HTMLElement | null;
    private $mainHeaderSubmenuBtnReturn: NodeListOf<HTMLElement> | null | undefined;

    private boundMobileClickMenuTogglerHandler: (() => void) | null = null;
    private boundHandleEscapeKey: ((evt: KeyboardEvent) => void) | null = null;
    private boundHandleEscapeKeyDesktop: ((evt: KeyboardEvent) => void) | null = null;
    private boundHandleClickOutside: ((event: MouseEvent) => void) | null = null;
    private boundInit: (() => void) | null = null;
    private submenuOpeners: Element[] = [];
    private boundSubmenuClickHandlers: Map<Element, (e: Event) => void> = new Map();
    private boundSubmenuKeydownHandlers: Map<Element, (e: Event) => void> = new Map();
    private boundHandleFocusChange: ((event: Event) => void) | null = null;
    private currentDevice: 'mobile' | 'desktop' | null = null;
    private scrollEventListener: boolean = false;
    private boundHandleMobileReturnClick: ((event: Event) => void) | null = null;
    private searchClearInitialized: boolean = false;

    constructor($el: HTMLElement | null) {
        this.$el = $el;
        this.$body = document.querySelector('body');
        this.$menuToggler = $el?.querySelector('.js-main-nav-burger-button') as HTMLButtonElement;
        this.$mainNav = $el?.querySelector('.main-nav') as HTMLElement;
        this.$mainNavContent = $el?.querySelector('.main-nav-content') as HTMLElement;
        this.$mainHeaderSubmenuBtnReturn = $el?.querySelectorAll('.js-btn-submenu-return');
        
        this.boundInit = this.init.bind(this);
        this.boundHandleEscapeKey = this.handleEscapeKey.bind(this);
        this.boundHandleEscapeKeyDesktop = this.handleEscapeKeyDesktop.bind(this);
        this.boundHandleClickOutside = this.handleClickOutside.bind(this);
        this.boundHandleFocusChange = this.handleFocusChange.bind(this);
        this.boundHandleMobileReturnClick = this.handleMobileReturnClick.bind(this);

        this.init();

        // Initialise les boutons clear pour les champs de recherche
        this.initSearchClearButtons();

        // Réinitialise le header au changement de taille de fenêtre
        let resizeTimer: ReturnType<typeof setTimeout>;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(this.boundInit!, 150);
        });
    }

    /**
     * Initialise le header dans le bon device
     */
    init() {
        const mobileBreakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-main-header')) + 1;
        const isMobile = window.innerWidth < mobileBreakpoint;
        
        // N'initialise que si le device change
        if (isMobile && this.currentDevice !== 'mobile') {
            this.destroyDesktop();
            this.initMobile();
            this.currentDevice = 'mobile';
        } else if (!isMobile && this.currentDevice !== 'desktop') {
            this.destroyMobile();
            this.initDesktop();
            this.currentDevice = 'desktop';
        }

        // Gestion du sticky header
        if (this.$body) {
            this.initStickyNavigation();
            this.initTransparentNavigation();
            if (!this.scrollEventListener) {
                window.addEventListener('scroll', () => {
                    this.initStickyNavigation();
                    this.initTransparentNavigation();
                });
                this.scrollEventListener = true;
            }
        }
    }

    /**
     * Initialise le header mobile
     */
    private initMobile() {
        // Ouverture / Fermeture du menu au clic sur le toggler
        if (this.$menuToggler) {
            this.boundMobileClickMenuTogglerHandler = this.mobileClickMenuTogglerHandler.bind(this);
            this.$menuToggler.addEventListener('click', this.boundMobileClickMenuTogglerHandler);
        }

        // Gestion des sous-menus
        this.submenuOpeners = Array.from(this.$el?.querySelectorAll('.js-main-menu-link-opener') || []);
        this.submenuOpeners.forEach(opener => {
            const clickHandler = (e: Event) => {
                const menuItem = opener.closest('.js-main-menu-item');
                const isOpen = menuItem?.classList.contains('open');

                // console.log('test click');
                
                
                // Toggle la classe open
                menuItem?.classList.toggle('open');
                
                // Met à jour les attributs aria
                opener.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
            };

            this.boundSubmenuClickHandlers.set(opener, clickHandler);
            opener.addEventListener('click', clickHandler);
        });

        this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
            if (this.boundHandleMobileReturnClick) {
                $link.addEventListener('click', this.boundHandleMobileReturnClick);
            }
        });

        // Fermeture du menu à l'appui sur Escape
        if (this.boundHandleEscapeKey) {
            document.addEventListener('keydown', this.boundHandleEscapeKey);
        }
    }

    /**
     * Initialise le header desktop
     */
    private initDesktop() {
        // Gestion des sous-menus
        this.submenuOpeners = Array.from(this.$el?.querySelectorAll('.js-main-menu-link-opener') || []);
        this.submenuOpeners.forEach(opener => {
            const clickHandler = (e: Event) => {
                e.preventDefault();
                this.toggleSubmenu(opener);
            };

            const keydownHandler = (e: Event) => {
                const keyEvent = e as KeyboardEvent;
                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                    e.preventDefault();
                    this.toggleSubmenu(opener);
                }
            };

            this.boundSubmenuClickHandlers.set(opener, clickHandler);
            this.boundSubmenuKeydownHandlers.set(opener, keydownHandler);

            opener.addEventListener('click', clickHandler);
            opener.addEventListener('keydown', keydownHandler);
        });

        // Gestion de la touche Escape pour fermer les sous-menus
        if (this.boundHandleEscapeKeyDesktop) {
            document.addEventListener('keydown', this.boundHandleEscapeKeyDesktop);
        }

        // Gestion du clic en dehors du menu
        if (this.boundHandleClickOutside) {
            document.addEventListener('click', this.boundHandleClickOutside);
        }

        // Gestion du focus pour fermer les sous-menus
        if (this.boundHandleFocusChange) {
            document.addEventListener('focusin', this.boundHandleFocusChange);
        }
    }

    /**
     * Gère le clic en dehors du menu
     */
    private handleClickOutside(event: MouseEvent) {
        if (!this.$mainNav) return;

        const target = event.target as Node;
        if (!this.$mainNav.contains(target)) {
            const openSubmenus = this.$el?.querySelectorAll('.js-main-menu-item.open');
            openSubmenus?.forEach(submenu => {
                const opener = submenu.querySelector('.js-main-menu-link-opener');
                if (opener) {
                    this.closeSubmenu(opener);
                }
            });
        }
    }

    /**
     * Gestion du clic sur le bouton burger
     */
    private mobileClickMenuTogglerHandler() {        
        if (this.$el?.classList.contains('open')) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Gestion de la touche Escape
     */
    private handleEscapeKey(evt: KeyboardEvent) {
        if (evt.key === 'Escape' && this.$el?.classList.contains('open')) {
            this.closeMobileMenu();
        }
    }

    /**
     * Gestion de la touche Escape en desktop
     */
    private handleEscapeKeyDesktop(evt: KeyboardEvent) {
        if (evt.key === 'Escape') {
            const openSubmenus = this.$el?.querySelectorAll('.js-main-menu-item.open');
            openSubmenus?.forEach(submenu => {
                const opener = submenu.querySelector('.js-main-menu-link-opener');
                if (opener) {
                    this.closeSubmenu(opener);
                }
            });
        }
    }

    /**
     * Gère les changements de focus pour fermer les sous-menus
     */
    private handleFocusChange(event: Event) {
        const focusedElement = event.target as Element;
        if (!focusedElement) return;

        // Vérifie tous les sous-menus ouverts
        const openSubmenus = this.$el?.querySelectorAll('.js-main-menu-item.open');
        if (!openSubmenus) return;

        openSubmenus.forEach(submenu => {
            const opener = submenu.querySelector('.js-main-menu-link-opener');
            if (!opener) return;

            // Vérifie si le focus est dans ce sous-menu ou sur son opener
            const isFocusInSubmenu = submenu.contains(focusedElement);
            const isFocusOnOpener = opener === focusedElement;

            // Si le focus n'est plus dans le sous-menu ni sur l'opener, on ferme le sous-menu
            if (!isFocusInSubmenu && !isFocusOnOpener) {
                this.closeSubmenu(opener);
            }
        });
    }

    /**
     * Ouvre ou ferme un sous-menu
     */
    private toggleSubmenu(opener: Element) {
        const menuItem = opener.closest('.js-main-menu-item');
        const isOpen = menuItem?.classList.contains('open');
        
        // Ferme les autres sous-menus ouverts
        const otherOpenMenus = this.$el?.querySelectorAll('.js-main-menu-item.open');
        otherOpenMenus?.forEach(menu => {
            if (menu !== menuItem) {
                const otherOpener = menu.querySelector('.js-main-menu-link-opener');
                if (otherOpener) {
                    this.closeSubmenu(otherOpener);
                }
            }
        });

        if (isOpen) {
            this.closeSubmenu(opener);
        } else {
            this.openSubmenu(opener);
        }
    }

    /**
     * Ouvre un sous-menu
     */
    private openSubmenu(opener: Element) {
        const menuItem = opener.closest('.js-main-menu-item');
        menuItem?.classList.add('open');
        opener.setAttribute('aria-expanded', 'true');
    }

    /**
     * Gère le clic sur les boutons de retour en mobile
     */
    private handleMobileReturnClick(event: Event): void {
        event.preventDefault();

        const trigger = event.currentTarget as HTMLElement | null;
        if (!trigger) return;

        const submenuWrapper = trigger.closest('.main-menu-submenu-wrapper');
        const menuItem = submenuWrapper?.closest('.js-main-menu-item');
        if (!menuItem) return;

        menuItem.classList.remove('open');

        const opener = menuItem.querySelector('.js-main-menu-link-opener') as HTMLElement | null;
        if (opener) {
            opener.setAttribute('aria-expanded', 'false');
            opener.focus();
        }

        const wrapper = menuItem.querySelector('.main-menu-submenu-wrapper');
        wrapper?.setAttribute('aria-hidden', 'true');

        if (this.$el) {
            FocusManager.trapFocus([this.$el]);
        }
    }

    /**
     * Ferme un sous-menu
     */
    private closeSubmenu(opener: Element) {
        const menuItem = opener.closest('.js-main-menu-item');
        menuItem?.classList.remove('open');
        opener.setAttribute('aria-expanded', 'false');
    }

    /**
     * Détruit le header mobile
     */
    private destroyMobile() {
        if (this.currentDevice !== 'mobile') return;

        ScrollManager.preventScroll(false);
        FocusManager.untrapFocus();

        this.$el?.classList.remove('open');
        this.$body?.classList.remove('menu-is-open');

        // Ferme tous les sous-menus
        const submenuItems = this.$el?.querySelectorAll('.js-main-menu-item');
        submenuItems?.forEach(item => {
            item.classList.remove('open');
            const opener = item.querySelector('.js-main-menu-link-opener');
            opener?.setAttribute('aria-expanded', 'false');
        });

        if (this.$menuToggler && this.boundMobileClickMenuTogglerHandler) {
            this.$menuToggler.removeEventListener('click', this.boundMobileClickMenuTogglerHandler);
            this.boundMobileClickMenuTogglerHandler = null;
        }

        if (this.boundHandleEscapeKey) {
            document.removeEventListener('keydown', this.boundHandleEscapeKey);
        }

        this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
            if (this.boundHandleMobileReturnClick) {
                $link.removeEventListener('click', this.boundHandleMobileReturnClick);
            }
        });

        this.cleanupSubmenuHandlers();
    }

    /**
     * Détruit le header desktop
     */
    private destroyDesktop() {
        if (this.currentDevice !== 'desktop') return;

        // Ferme tous les sous-menus
        const submenuItems = this.$el?.querySelectorAll('.js-main-menu-item');
        submenuItems?.forEach(item => {
            item.classList.remove('open');
            const opener = item.querySelector('.js-main-menu-link-opener');
            opener?.setAttribute('aria-expanded', 'false');
        });

        if (this.boundHandleEscapeKeyDesktop) {
            document.removeEventListener('keydown', this.boundHandleEscapeKeyDesktop);
        }
        if (this.boundHandleClickOutside) {
            document.removeEventListener('click', this.boundHandleClickOutside);
        }
        if (this.boundHandleFocusChange) {
            document.removeEventListener('focusin', this.boundHandleFocusChange);
        }

        this.cleanupSubmenuHandlers();
    }

    /**
     * Gère la classe header-transparent sur la navigation principale
     */
    private initTransparentNavigation() {
        if (!this.isTransparentNavigationApplicable()) {
            this.$el?.classList.remove('header-transparent');
            return;
        }

        if (window.pageYOffset > 20) {
            this.$el?.classList.remove('header-transparent');
        } else {
            this.$el?.classList.add('header-transparent');
        }
    }

    private isTransparentNavigationApplicable(): boolean {
        const body = this.$body;
        if (!body) {
            return false;
        }

        const isPageWithHeroImage = body.classList.contains('page') &&
            body.querySelector('main > .block-hero-text-image') !== null;

        if (isPageWithHeroImage) {
            return false;
        }

        return this.applicableHeaderTransparent.some(token => body.classList.contains(token));
    }

    /**
     * Initialise le header sticky
     */
    private initStickyNavigation() {
        if (window.pageYOffset > 20) {
            this.$el?.classList.add('main-header--sticky');
            this.$body?.classList.add('main-header--is-sticky');
        } else {
            this.$el?.classList.remove('main-header--sticky');
            this.$body?.classList.remove('main-header--is-sticky');
        }
    }

    /**
     * Ouvre le menu mobile
     */
    private openMobileMenu() {
        this.$el?.classList.add('open');
        this.$body?.classList.add('menu-is-open');
        
        ScrollManager.preventScroll(true);
        
        // On s'assure que tous les éléments nécessaires sont présents
        if (this.$mainNav && this.$menuToggler && this.$mainNavContent) {
            // Mise à jour de l'attribut aria-expanded avant d'initialiser le focus trap
            this.$menuToggler.setAttribute('aria-expanded', 'true');

            FocusManager.trapFocus(
                [this.$el]
            );
        }
    }

    /**
     * Ferme le menu mobile
     */
    private closeMobileMenu() {
        this.$el?.classList.remove('open');
        this.$body?.classList.remove('menu-is-open');
        const $submenus = this.$el?.querySelectorAll('.js-main-menu-item.open');
        
        $submenus?.forEach(submenu => {
            submenu.classList.remove('open');
            const opener = submenu.querySelector('.js-main-menu-link-opener');
            opener?.setAttribute('aria-expanded', 'false');
        });

        ScrollManager.preventScroll(false);
        FocusManager.untrapFocus();

        // Mise à jour de l'attribut aria-expanded
        this.$menuToggler?.setAttribute('aria-expanded', 'false');
        
        // Remet le focus sur le bouton burger
        this.$menuToggler?.focus();
    }

    private cleanupSubmenuHandlers() {
        this.submenuOpeners.forEach(opener => {
            const clickHandler = this.boundSubmenuClickHandlers.get(opener);
            const keydownHandler = this.boundSubmenuKeydownHandlers.get(opener);

            if (clickHandler) {
                opener.removeEventListener('click', clickHandler);
            }
            if (keydownHandler) {
                opener.removeEventListener('keydown', keydownHandler);
            }
        });

        this.boundSubmenuClickHandlers.clear();
        this.boundSubmenuKeydownHandlers.clear();
        this.submenuOpeners = [];
    }

    private initSearchClearButtons(): void {
        if (this.searchClearInitialized) return;
        this.searchClearInitialized = true;

        const searchInputs = Array.from(document.querySelectorAll<HTMLInputElement>('.main-nav-search-input'));

        searchInputs.forEach((input) => {
            const wrapper = input.closest('.input-wrapper');
            const clearBtn = wrapper?.querySelector<HTMLButtonElement>('.search-clear');

            const toggleClearBtn = () => {
                if (!clearBtn) return;
                clearBtn.style.display = input.value && input.value.length ? 'flex' : 'none';
            };

            toggleClearBtn();

            input.addEventListener('input', toggleClearBtn);

            if (clearBtn) {
                clearBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    input.value = '';
                    input.dispatchEvent(new Event('input'));
                    input.focus();
                    toggleClearBtn();
                });
            }
        });
    }
}
